import { TF } from 'react-translator-component';

import ccstype1 from '../../../../assets/allConnectorsPng/ccstype1.png';
import ccstype2 from '../../../../assets/allConnectorsPng/ccstype2.png';
import chademo from '../../../../assets/allConnectorsPng/chademo.png';
import gbtac from '../../../../assets/allConnectorsPng/gbTAC.png';
import tesla from '../../../../assets/allConnectorsPng/tesla.png';
import type1 from '../../../../assets/allConnectorsPng/type1.png';
import type2 from '../../../../assets/allConnectorsPng/type2.png';
import plugNbr from '../../../../assets/plug-nbr.png';
import {
  StationConnector,
  StationConnectorStatus,
} from '../../../../types/responses/Interoperability';

/**
 * Os conectores no detalhe da estação (MV2-1706).
 *
 * O parceiro escolhe a estação pelo conector: tipo errado é viagem perdida, e potência
 * ausente é promessa que ninguém fez. Nada aqui inventa valor no lugar do que falta —
 * "sem potência" é informação, "0 kW" é informação errada.
 */

const STATUS_LABELS: Record<StationConnectorStatus, string> = {
  AVAILABLE: 'Available',
  PREPARING: 'Preparing',
  CHARGING: 'Charging',
  SUSPENDEDEV: 'Suspended by the vehicle',
  SUSPENDEDEVSE: 'Suspended by the station',
  FINISHING: 'Finishing',
  RESERVED: 'Reserved',
  FAULTED: 'Faulted',
  UNAVAILABLE: 'Unavailable',
};

/**
 * Cor por estado.
 *
 * Verde para o que atende agora, azul para o que está em uso, vermelho para falha,
 * cinza para o que não atende. Nunca reportou fica cinza-escuro e com texto próprio: é
 * ausência de informação, não estado.
 */
export const CONNECTOR_STATUS_STYLE: Record<
  StationConnectorStatus,
  { bg: string; fg: string; dot: string }
> = {
  AVAILABLE: { bg: '#e7f6ed', fg: '#1e7a45', dot: '#26a35c' },
  PREPARING: { bg: '#eaf1fd', fg: '#1e4f9c', dot: '#2f6fd0' },
  CHARGING: { bg: '#eaf1fd', fg: '#1e4f9c', dot: '#2f6fd0' },
  SUSPENDEDEV: { bg: '#fdeee4', fg: '#a8500f', dot: '#e0700f' },
  SUSPENDEDEVSE: { bg: '#fdeee4', fg: '#a8500f', dot: '#e0700f' },
  FINISHING: { bg: '#eaf1fd', fg: '#1e4f9c', dot: '#2f6fd0' },
  RESERVED: { bg: '#f3eefd', fg: '#5b3ba8', dot: '#7a56d1' },
  FAULTED: { bg: '#fdecec', fg: '#b02525', dot: '#c8352f' },
  UNAVAILABLE: { bg: '#eef0f2', fg: '#6b7280', dot: '#9aa3af' },
};

export const NEVER_REPORTED_STYLE = { bg: '#eef0f2', fg: '#4a5462', dot: '#757b88' };

export const connectorStatusLabel = (status: StationConnectorStatus | null): string => {
  if (!status) {
    // Diferente de indisponível: indisponível é operação, nunca ter reportado é
    // instalação ou cadastro que ninguém terminou.
    return TF('never reported');
  }

  return TF(STATUS_LABELS[status] || status);
};

export const connectorStatusStyle = (
  status: StationConnectorStatus | null,
): { bg: string; fg: string; dot: string } =>
  status ? CONNECTOR_STATUS_STYLE[status] || NEVER_REPORTED_STYLE : NEVER_REPORTED_STYLE;

/** "22 kW", "32 A", ou o valor cru quando ninguém disse a unidade. */
export const connectorPower = (connector: StationConnector): string => {
  if (connector.power_max_value === null || connector.power_max_value === undefined) {
    return TF('no power');
  }

  const unit = connector.power_max_type === 'KW' ? 'kW' : connector.power_max_type;

  // Valor sem unidade sai sozinho: a coluna aceita A e KW, e anunciar 32 A como
  // 32 kW seria mentira grave para quem escolhe a estação pelo conector.
  return unit ? `${connector.power_max_value} ${unit}` : `${connector.power_max_value}`;
};

/**
 * Nome e figura por tipo, nas chaves do ConnectorTypeEnum do serviço — que é o que chega
 * no campo, cru.
 *
 * As imagens são as mesmas que o resto do dashboard usa nas tabelas de carregador
 * (assets/allConnectorsPng), e não um desenho novo: o mesmo plugue tem de ter a mesma cara
 * nas duas telas, senão viram dois conectores diferentes aos olhos de quem opera.
 *
 * GBT usa a figura AC porque o próprio enum do serviço o declara como
 * alternating_current — existe também a versão DC do desenho, e escolher a errada seria
 * anunciar corrente que a estação não entrega.
 */
const TYPES: Record<string, { label: string; image: string }> = {
  TYPE_1: { label: 'Type 1', image: type1 },
  TYPE_2: { label: 'Type 2', image: type2 },
  CCS_TYPE_1: { label: 'CCS Type 1', image: ccstype1 },
  CCS_TYPE_2: { label: 'CCS Type 2', image: ccstype2 },
  CHADEMO: { label: 'CHAdeMO', image: chademo },
  TESLA: { label: 'Tesla', image: tesla },
  GBT: { label: 'GB/T', image: gbtac },
  PLUG_NBR: { label: 'Plug NBR', image: plugNbr },
};

/**
 * O nome do tipo como gente lê.
 *
 * Tipo que a tela ainda não conheça sai cru: quem confere cadastro precisa ver que existe,
 * e esconder seria pior do que mostrar TYPE_9 na tela.
 */
export const connectorTypeLabel = (connector: StationConnector): string => {
  if (!connector.connector_type) {
    return TF('no type');
  }

  return TYPES[connector.connector_type]?.label || connector.connector_type;
};

/** A figura do plugue, ou null — nunca um <img> apontando para nada. */
export const connectorImage = (type: string | null | undefined): string | null =>
  (type && TYPES[type]?.image) || null;

/** "Conector 1" — o número é como o parceiro identifica o conector no OCPP. */
export const connectorTitle = (connector: StationConnector): string =>
  `${TF('Connector')} ${connector.seq ?? '—'}`;
